import {
  ADDRESS_ID,
  APPLYING_BONUS_NAME,
  ActiveModes,
  Address,
  OccCustomerConsignment,
  Options,
  PAYMENT_NAME,
  PICKUP,
  PaymentMode,
  RECEIPT_TYPE_NAME,
  ReceiptType,
  TIME_SLOTS_NAME,
  TimeSlot,
  TimeSlots,
} from '@checkout/const';
import { MemoizedSelector, createFeatureSelector, createSelector } from '@ngrx/store';
import { CustomOrder } from '@order/const';
import { Pos } from '@shop/const';
import { StateUtils } from '@spartacus/core';
import { CHECKOUT_FEATURE, CustomCheckoutState, StateWithCustomCheckout } from '../checkout-state';

export const getCustomCheckoutState: MemoizedSelector<StateWithCustomCheckout, CustomCheckoutState> =
  createFeatureSelector<CustomCheckoutState>(CHECKOUT_FEATURE);

export const getReceiptTypes: MemoizedSelector<StateWithCustomCheckout, ReceiptType[]> = createSelector(
  getCustomCheckoutState,
  (state: CustomCheckoutState) => StateUtils.loaderValueSelector(state.receiptType),
);

export const getActiveModes: MemoizedSelector<StateWithCustomCheckout, ActiveModes> = createSelector(
  getCustomCheckoutState,
  (state: CustomCheckoutState) => state?.activeModes || {},
);

export const getApplyingBonus: MemoizedSelector<StateWithCustomCheckout, number> = createSelector(
  getActiveModes,
  (activeModes: ActiveModes) => activeModes[APPLYING_BONUS_NAME],
);

export const getActiveReceiptType: MemoizedSelector<StateWithCustomCheckout, string> = createSelector(
  getActiveModes,
  (activeModes: ActiveModes) => activeModes[RECEIPT_TYPE_NAME],
);

export const getActivePickup: MemoizedSelector<StateWithCustomCheckout, string | undefined> = createSelector(
  getActiveModes,
  (activeModes: ActiveModes) => activeModes[PICKUP],
);

export const getActiveAddressId: MemoizedSelector<StateWithCustomCheckout, string | undefined> = createSelector(
  getActiveModes,
  (activeModes: ActiveModes) => activeModes[ADDRESS_ID],
);

export const getActivePaymentCode: MemoizedSelector<StateWithCustomCheckout, string | undefined> = createSelector(
  getActiveModes,
  (activeModes: ActiveModes) => activeModes[PAYMENT_NAME],
);

export const getActiveTimeSlot: MemoizedSelector<StateWithCustomCheckout, TimeSlot> = createSelector(
  getActiveModes,
  (activeModes: ActiveModes) => activeModes[TIME_SLOTS_NAME],
);

export const getTimeSlotsState: MemoizedSelector<
  StateWithCustomCheckout,
  StateUtils.LoaderState<TimeSlots>
> = createSelector(getCustomCheckoutState, (state: CustomCheckoutState) => {
  return state.timeSlots;
});

export const getTimeSlots: MemoizedSelector<StateWithCustomCheckout, TimeSlots> = createSelector(
  getTimeSlotsState,
  (state: StateUtils.LoaderState<TimeSlots>) => StateUtils.loaderValueSelector(state),
);

export const getTimeSlotsLoading: MemoizedSelector<StateWithCustomCheckout, boolean> = createSelector(
  getTimeSlotsState,
  (state: StateUtils.LoaderState<TimeSlots>) => StateUtils.loaderLoadingSelector(state),
);

export const getAddressList: MemoizedSelector<StateWithCustomCheckout, Address[]> = createSelector(
  getCustomCheckoutState,
  (state: CustomCheckoutState) => StateUtils.loaderValueSelector(state.addressList)?.addresses,
);

export const getOptions: MemoizedSelector<StateWithCustomCheckout, Options> = createSelector(
  getCustomCheckoutState,
  (state: CustomCheckoutState) => StateUtils.loaderValueSelector(state.options),
);

export const getAddressListLoading: MemoizedSelector<StateWithCustomCheckout, boolean> = createSelector(
  getCustomCheckoutState,
  (state: CustomCheckoutState) => state.addressList.loading,
);

export const getAddressListSuccess: MemoizedSelector<StateWithCustomCheckout, boolean> = createSelector(
  getCustomCheckoutState,
  (state: CustomCheckoutState) => StateUtils.loaderLoadingSelector(state.addressList),
);

export const getOptionsLoading: MemoizedSelector<StateWithCustomCheckout, boolean> = createSelector(
  getCustomCheckoutState,
  (state: CustomCheckoutState) => state.options.loading,
);

export const getActiveOptions: MemoizedSelector<StateWithCustomCheckout, any> = createSelector(
  getCustomCheckoutState,
  (state: CustomCheckoutState) => state?.activeModes?.options,
);

export const getRedirectedToPaymentPage: MemoizedSelector<StateWithCustomCheckout, boolean> = createSelector(
  getCustomCheckoutState,
  (state: CustomCheckoutState) => state.redirectedToPaymentPage.value,
);

export const getOrderDetails: MemoizedSelector<StateWithCustomCheckout, CustomOrder> = createSelector(
  getCustomCheckoutState,
  (state: CustomCheckoutState) => state.orderDetails,
);

export const getUserPickupShopListState: MemoizedSelector<
  StateWithCustomCheckout,
  StateUtils.LoaderState<Pos[]>
> = createSelector(getCustomCheckoutState, (state: CustomCheckoutState) => state.userPickupShopList);

export const getUserPickupShopListValue: MemoizedSelector<StateWithCustomCheckout, Pos[]> = createSelector(
  getCustomCheckoutState,
  (state: CustomCheckoutState) => StateUtils.loaderValueSelector(state.userPickupShopList),
);

export const getUserPickupShopListLoading: MemoizedSelector<StateWithCustomCheckout, boolean> = createSelector(
  getCustomCheckoutState,
  (state: CustomCheckoutState) => StateUtils.loaderLoadingSelector(state.userPickupShopList),
);

export const getUserPickupShopListSuccess: MemoizedSelector<StateWithCustomCheckout, boolean> = createSelector(
  getCustomCheckoutState,
  (state: CustomCheckoutState) => StateUtils.loaderSuccessSelector(state.userPickupShopList),
);

export const getUserPickupShopListError: MemoizedSelector<StateWithCustomCheckout, boolean> = createSelector(
  getCustomCheckoutState,
  (state: CustomCheckoutState) => StateUtils.loaderErrorSelector(state.userPickupShopList),
);

export const getPaymentModeListValue: MemoizedSelector<StateWithCustomCheckout, PaymentMode[]> = createSelector(
  getCustomCheckoutState,
  (state: CustomCheckoutState) => StateUtils.loaderValueSelector(state.paymentModeList) || [],
);

export const getPaymentModeListLoading: MemoizedSelector<StateWithCustomCheckout, boolean> = createSelector(
  getCustomCheckoutState,
  (state: CustomCheckoutState) => StateUtils.loaderLoadingSelector(state.paymentModeList),
);

export const getPaymentModeListSuccess: MemoizedSelector<StateWithCustomCheckout, boolean> = createSelector(
  getCustomCheckoutState,
  (state: CustomCheckoutState) => StateUtils.loaderSuccessSelector(state.paymentModeList),
);

export const getPaymentModeListError: MemoizedSelector<StateWithCustomCheckout, boolean> = createSelector(
  getCustomCheckoutState,
  (state: CustomCheckoutState) => StateUtils.loaderErrorSelector(state.paymentModeList),
);

export const getCustomerConsignmentState: MemoizedSelector<
  StateWithCustomCheckout,
  StateUtils.LoaderState<OccCustomerConsignment>
> = createSelector(getCustomCheckoutState, (state: CustomCheckoutState) => state.customerConsignment);

export const getCustomerConsignmentValue: MemoizedSelector<StateWithCustomCheckout, OccCustomerConsignment> =
  createSelector(getCustomerConsignmentState, (state: StateUtils.LoaderState<OccCustomerConsignment>) =>
    StateUtils.loaderValueSelector(state),
  );

export const getCustomerConsignmentLoading: MemoizedSelector<StateWithCustomCheckout, boolean> = createSelector(
  getCustomerConsignmentState,
  (state: StateUtils.LoaderState<OccCustomerConsignment>) => StateUtils.loaderLoadingSelector(state),
);

export const getCustomerConsignmentSuccess: MemoizedSelector<StateWithCustomCheckout, boolean> = createSelector(
  getCustomerConsignmentState,
  (state: StateUtils.LoaderState<OccCustomerConsignment>) => StateUtils.loaderSuccessSelector(state),
);
